/**
 * Cart Retail Total Sync (MA-3016)
 *
 * Keeps the hidden `_CLP4` line-item property (RETAIL_TOTAL_PROPERTY, see
 * assets/utilities.js) in sync on every merchandise line in the cart for the
 * OneSource tax engine:
 *
 *   - PPC/UFO customers (customer.metafields.custom.is_ppc): every line carries
 *     `_CLP4` = retail unit price × quantity, formatted "123.45". The retail
 *     price comes from the line variant's customMA.retailSalePrice (or
 *     retailOriginalPrice) metafield, not the distributor price the PPC
 *     customer actually pays.
 *   - Everyone else: `_CLP4` is absent. A line that still carries it (e.g. a
 *     tier swap after logout kept the properties) has it removed.
 *
 * The retail price can't be read from the AJAX Cart API (it carries no
 * metafields), so it's rendered server-side into the
 * `data-retail-price` attribute (cents) on each cart row `<tr>`
 * (snippets/cart-products.liquid), keyed by `data-variant-id`, and refreshed
 * whenever the cart drawer/page sections re-render. A line whose variant has
 * no rendered price yet is left alone until the next `cart:update`.
 *
 * Runs once on load and again on every `cart:update` — which every add,
 * quantity change and removal dispatches, so the total follows the quantity.
 * A correction is applied one line at a time via /cart/change.js — a property
 * write changes the line's key, so each write re-picks its target from the
 * freshest cart response, same as cart-test-customer-sync.js. Writes are
 * serialized through a shared queue so overlapping triggers never race each
 * other's line keys.
 */

import { fetchConfig, RETAIL_TOTAL_PROPERTY } from "@theme/utilities";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { isBcrsFeeLine, visibleItemCount } from "@theme/bcrs-fee";

/** `sourceId` of the re-render this module dispatches, so its own listener skips it. */
const STAMP_SOURCE_ID = "cart-retail-total-sync";

/** Serializes writes so overlapping triggers never race the same line keys. */
let syncQueue = Promise.resolve();

/** Whether the current customer is PPC/UFO, from the ma:customer meta tag. */
function isPpcCustomer() {
  return document.querySelector('meta[name="ma:customer"]')?.getAttribute("data-is-ppc") === "true";
}

/**
 * Retail unit price (cents) per variant id, read from every rendered cart row
 * across all <cart-items-component> instances (drawer + cart page).
 * @returns {Map<string, number>}
 */
function collectRetailPrices() {
  const prices = new Map();
  for (const row of document.querySelectorAll("cart-items-component [data-variant-id][data-retail-price]")) {
    const variantId = row.getAttribute("data-variant-id");
    const cents = Number.parseInt(row.getAttribute("data-retail-price") ?? "", 10);
    if (variantId && Number.isFinite(cents)) prices.set(variantId, cents);
  }
  return prices;
}

/**
 * Desired `_CLP4` for a line: the formatted retail total for PPC customers,
 * `null` when the property must be absent, `undefined` when it can't be
 * resolved yet (no rendered retail price for the variant).
 * @param {any} item A cart line item.
 * @param {boolean} isPpc
 * @param {Map<string, number>} prices
 * @returns {string | null | undefined}
 */
function desiredValue(item, isPpc, prices) {
  if (!isPpc) return null;
  const cents = prices.get(String(item.variant_id));
  if (cents === undefined) return undefined;
  return ((cents * item.quantity) / 100).toFixed(2);
}

/**
 * @param {any} item A cart line item.
 * @param {boolean} isPpc
 * @param {Map<string, number>} prices
 * @returns {boolean}
 */
function lineNeedsStamp(item, isPpc, prices) {
  if (isBcrsFeeLine(item)) return false;
  const properties = item.properties ?? {};
  const hasProperty = Object.prototype.hasOwnProperty.call(properties, RETAIL_TOTAL_PROPERTY);
  const desired = desiredValue(item, isPpc, prices);
  if (desired === undefined) return false;
  if (desired === null) return hasProperty;
  return !hasProperty || properties[RETAIL_TOTAL_PROPERTY] !== desired;
}

/** Section ids of every cart surface on the page, so a correction re-renders them in the same round trip. */
function collectCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll("cart-items-component")) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * Applies the desired `_CLP4` to every merchandise line that doesn't already
 * carry it, one /cart/change.js call at a time (a property write changes the line key).
 */
async function reconcile() {
  let cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: "application/json" } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  if (!cart || !Array.isArray(cart.items)) return;

  const isPpc = isPpcCustomer();
  const prices = collectRetailPrices();
  const sectionIds = collectCartSectionIds();
  let changed = false;

  const needsStamp = (/** @type {any} */ it) => lineNeedsStamp(it, isPpc, prices);

  for (let attempts = cart.items.filter(needsStamp).length; attempts > 0; attempts--) {
    const item = cart.items.find(needsStamp);
    if (!item) break;

    const properties = { ...item.properties };
    const desired = desiredValue(item, isPpc, prices);
    if (desired === null) {
      delete properties[RETAIL_TOTAL_PROPERTY];
    } else {
      properties[RETAIL_TOTAL_PROPERTY] = desired;
    }
    const remaining = cart.items.filter(needsStamp).length;

    /** @type {Record<string, unknown>} */
    const body = { id: item.key, quantity: item.quantity, properties };
    if (remaining === 1 && sectionIds.length) {
      body.sections = sectionIds.join(",");
      body.sections_url = window.location.pathname;
    }

    const updated = await fetch(Theme.routes.cart_change_url, fetchConfig("json", { body: JSON.stringify(body) }))
      .then((r) => r.json())
      .catch(() => null);
    if (!updated || updated.status || !Array.isArray(updated.items)) {
      console.error(
        "[cart-retail-total-sync] Failed to update line property:",
        updated?.description ?? "unknown error"
      );
      break;
    }
    cart = updated;
    changed = true;
  }

  if (!changed) return;

  const autoOpenDrawers = Array.from(document.querySelectorAll("cart-drawer-component[auto-open]"));
  for (const drawer of autoOpenDrawers) drawer.removeAttribute("auto-open");

  document.dispatchEvent(
    new CartUpdateEvent(cart, STAMP_SOURCE_ID, {
      source: STAMP_SOURCE_ID,
      itemCount: visibleItemCount(cart),
      sections: cart.sections ?? undefined,
    })
  );

  setTimeout(() => {
    for (const drawer of autoOpenDrawers) drawer.setAttribute("auto-open", "");
  }, 0);
}

/**
 * @param {(Event & { detail?: { sourceId?: string } }) | undefined} [event]
 */
function sync(event) {
  if (event?.detail?.sourceId === STAMP_SOURCE_ID) return;

  syncQueue = syncQueue
    .then(reconcile)
    .catch((err) => console.error("[cart-retail-total-sync] Sync failed:", err));
}

document.addEventListener(ThemeEvents.cartUpdate, sync);
sync();
